import {_, action, autorun, computed, computedFn, IDisposer, jsondiff, observable, React, utils, when} from '@api'
import {IContextMenuItems, IFlexLayoutWhiteboardTabConfigSettings, VipLocalStore} from '@modules'
import {AlignType, ArrowShape, ArrowUtil, CatalogItemCardShape, CatalogItemShapeStore, CatalogItemShapeUtil, Commands, Decoration, DEFAULT_GRID_SIZE, DEFAULT_STROKE_COLOR, DEFAULT_STROKE_WIDTH, DEFAULT_TEXT_COLOR, DistributeType, DrawShape, DrawUtil, EllipseUtil, FlipType, FrameShapeStore, FrameUtil, GroupUtil, ImageShape, ImageUtil, MoveType, RectangleUtil, SelectedShapesFloatingToolbarStore, SelectedShapesTitleUtil, ShapeComponentStore, StickyUtil, StretchType, TDPage, TDShapeUtil, TDShapeUtilsMap, TDToolType, TextShape, TextUtil, TLDR, TLHandle, ToolStatus, WhiteboardCameraStore, WhiteboardClipboardStore, WhiteboardContextMenu, WhiteboardFlexLayoutTabStore, WhiteboardPageMenuStore, WhiteboardPageStore, WhiteboardSelectionStore, WhiteboardToolStore} from '@modules/whiteboard'
import {HighlighterUtil} from '@modules/whiteboard/shapes/highlighter'
import {createDefaultStyle, ShapeStyles} from '@modules/whiteboard/shapes/shared/shape-styles'
import {TDBinding, TDShape, TDShapeType} from '@modules/whiteboard/shapes/types'
import {WhiteboardShapeUtilHelper} from '@modules/whiteboard/WhiteboardShapeUtilHelper'
import {globalStores, StoresInstance} from '@stores'
import {TLBinding, TLBounds, TLBoundsCorner, TLBoundsEventHandler, TLBoundsHandle, TLBoundsHandleEventHandler, TLCanvasEventHandler, TLKeyboardEventHandler, TLPage, TLPageState, TLPinchEventHandler, TLPointerEventHandler, TLShapeCloneHandler, TLShapeUtilsMap, TLSnapLine, TLWheelEventHandler, Utils} from '@tldraw/core'
import {Vec} from '@tldraw/vec'
import {guid} from '@utils'
import {IObservableArray, reaction, runInAction, toJS} from 'mobx'
import {StrokeOptions} from 'perfect-freehand'
import {WhiteboardCreateShapesStore} from './WhiteboardCreateShapesStore'
import {WhiteboardPreferencesStore} from './WhiteboardPreferencesStore'

export type TDSnapshot = IFlexLayoutWhiteboardTabConfigSettings
export type WhiteboardPage = TLPage<WhiteboardShape, TDBinding>
export type WhiteboardPageDataEntryState = { page: TLPage<WhiteboardShape, TDBinding>, state: WhiteboardPageState }
export type WhiteboardPagesState = { [id: string]: WhiteboardPageDataEntryState }
export type WhiteboardShape = TDShape
export type WhiteboardPageState = {
  history?: Array<TLPage<WhiteboardShape, TDBinding>>
  background?: 'none' | 'blueprint' | 'checkerboard'
  selectedIds?: string[]
  camera: {
    point: number[]
    zoom: number
  }
}

export const DEFAULT_WHITEBOARD_PAGE_CONTENT: WhiteboardPageDataEntryState = {
  page: {
    id: 'page1',
    shapes: {},
    bindings: {}
  },
  state: {
    history: [],
    camera: {
      point: [0, 0],
      zoom: 1
    }
  }
}

export const DEFAULT_WHITEBOARD_TAB_SETTINGS: IFlexLayoutWhiteboardTabConfigSettings = {
  meta: {isDarkMode: false},
  pages: {
    'page1': DEFAULT_WHITEBOARD_PAGE_CONTENT
  },
  ui: {
    isPenMode: false,
    isDarkMode: false,
    isZoomSnap: false,
    isFocusMode: false,
    isSnapping: true,
    isDebugMode: false,
    isReadonlyMode: false,
    nudgeDistanceLarge: 16,
    nudgeDistanceSmall: 1,
    showRotateHandles: true,
    showBindingHandles: true,
    showCloneHandles: false,
    showGrid: false,
    gridSize: DEFAULT_GRID_SIZE,
    currentPageId: 'page1',
    currentStyle: createDefaultStyle(),
    viewport: Utils.getBoundsFromPoints([
      [0, 0],
      [100, 100]
    ]),

    selectHistory: {
      stack: [[]] as string[][],
      pointer: 0
    },

    rotationInfo: {
      selectedIds: [] as string[],
      center: [0, 0]
    },

    pasteInfo: {
      center: [0, 0],
      offset: [0, 0]
    }
  }
}

export interface WhiteboardAppState {
  hoveredId?: string
  isStyleOpen: boolean
  status: ToolStatus
  snapLines: IObservableArray<TLSnapLine>,
  editingId?: string,
  bindingId?: string,
  brush?: TLBounds | null,
  pointedId?: string,
  activeTool?: string,
  isToolLocked?: boolean,
  rendererBounds?: TLBounds,
  showZoomExpandedMenu?: boolean
  toolSettings?: {
    draw?: StrokeOptions
  }
}

export class WhiteboardStore extends VipLocalStore implements IContextMenuItems {
  private _debounceUpdateClientRect = _.debounce(() => {
    this.domContainerRect = this.container.getBoundingClientRect()
  })

  @observable.ref domContainerRect: DOMRect
  @observable.ref toolbar: SelectedShapesFloatingToolbarStore
  @observable.ref pageMenu: WhiteboardPageMenuStore

  get state(): TDSnapshot { return this.settings}

  constructor(stores: StoresInstance, public props: { tabStore: WhiteboardFlexLayoutTabStore }) {
    super(stores)

    _.merge(this.appState, props.tabStore.tabSettings.ui)

    this.shapeUtil = new WhiteboardShapeUtilHelper(this)
    this.createUtil = new WhiteboardCreateShapesStore(this)
    this.camera = new WhiteboardCameraStore(this)
    this.selection = new WhiteboardSelectionStore(this)
    this.tools = new WhiteboardToolStore(this)
    this.clipboard = new WhiteboardClipboardStore(this)
    this.preferences = new WhiteboardPreferencesStore(this)
    this.pageMenu = new WhiteboardPageMenuStore(this)

    _.merge(this.pageState, _.cloneDeep(this._combinedPageState))
    this.cleanupPageState()
  }

  /// region Shape Utils

  @computed get shapeUtils(): TDShapeUtilsMap<TDShape> {
    return {
      rectangle: new RectangleUtil(this),
      ellipse: new EllipseUtil(this),
      draw: new DrawUtil(this),
      arrow: new ArrowUtil(this),
      text: new TextUtil(this),
      group: new GroupUtil(this),
      sticky: new StickyUtil(this),
      catalogItem: new CatalogItemShapeUtil(this),
      selectedShapesTitle: new SelectedShapesTitleUtil(this),
      image: new ImageUtil(this),
      frame: new FrameUtil(this),
      highlighter: new HighlighterUtil(this) as any
    }
  }

  shapeUtil: WhiteboardShapeUtilHelper

  /// endregion
  @observable.shallow shapeComponentStores: { [id: string]: any } = {}

  @computed get selectedShapeComponentStores() {
    const {selectedShapes, shapeComponentStores} = this
    return selectedShapes.map(s => shapeComponentStores[s.id])
  }

  shapeComponentStoreFor = <S extends TDShape, T extends ShapeComponentStore<S, any>>(shape: S) : T => {
    return this.shapeComponentStores[shape.id] as T
  }

  createUtil: WhiteboardCreateShapesStore
  clipboard: WhiteboardClipboardStore
  camera: WhiteboardCameraStore
  selection: WhiteboardSelectionStore
  tools: WhiteboardToolStore
  preferences: WhiteboardPreferencesStore

  onContextMenu = (e) => {
    this.stores.contextMenu.onComponentContextMenu(e, this)
  }

  @action onMouseLeave = () => this.mouseLocationRaw = null
  @action onMouseMove = (e: React.MouseEvent) => {
    const {domContainerRect} = this
    this.mouseLocationRaw = {x: e.clientX, y: e.clientY}
  }

  @computed get contextMenuItems() {
    return (
      <WhiteboardStoreContext.Provider value={this}>
        <WhiteboardContextMenu/>
      </WhiteboardStoreContext.Provider>)
  }

  /**
   * Reset the document to a blank state.
   */
  @action resetDocument = () => {
    const {appState, clipboard} = this
    clipboard.pasteInfo.offset = [0, 0]
    appState.activeTool = 'select'
    this.store.tabConfig.whiteboard = _.cloneDeep(DEFAULT_WHITEBOARD_TAB_SETTINGS)
  }

  @computed get hasSelection() {
    const {appState: {status}, selectedIds} = this

    return selectedIds.length > 0
  }

  @computed get visibleShapes() {
    return this.shapes.filter(s => !s.isHidden)
  }

  @computed get isEmptyCanvas() {
    const {shapesOnScreen} = this
    return shapesOnScreen.filter(s => !s.isHidden).length == 0
  }

  get tab() {
    return this.vipTab?.tab
  }

  get vipTab() {
    return this.stores.vipTabs['whiteboard']
  }

  @computed get serializableShapes() {
    const {shapes} = this
    return shapes.filter(s => !s.isGenerated && _.get(s, 'isComplete') !== false)
  }

  get history() {
    return this.myPageState.history ?? []
  }

  set history(v) {
    this.myPageState.history = v
  }

  registerEffects = () => {
    const {camera, selection} = this

    let tempViewport: TLBounds

    const setInitialBoundsDisposer = reaction(() => this.appState.rendererBounds, () => {
      runInAction(() => {
        camera.zoomTo(this.myPageState.camera.zoom)
        camera.initialized = true
        setInitialBoundsDisposer()
      })
    }, {name: `On initial set of bounds, zoom to our saved zoom level to setup the viewport`})

    const _toDispose: Array<IDisposer> = [
      setInitialBoundsDisposer,
      when(() => this.tab != null, () => this.tab.extraData.whiteboard = this),
      autorun(() => {
        if (!this.isInSession) {
          const {shapes, shapeUtilFor, camera: {viewport}} = this
          this.shapesOnScreen = shapes.filter(s => shapeUtilFor(s).hitTestBounds(s, viewport))
        }
      }, {delay: 250}),
      autorun(() => {
        const {page, shapeUtilFor, shapes} = this
        if (!page) return
        runInAction(() => {
          shapes.forEach(shape => {
            if (!shape.type || !shapeUtilFor(shape)) {
              delete shapes[shape.id]
            }

            !shape.style?.strokeColor && (_.merge(shape.style, {strokeColor: DEFAULT_STROKE_COLOR}))
            !shape.style?.textColor && (_.merge(shape.style, {textColor: DEFAULT_TEXT_COLOR}))
            //!shape.style?.backgroundColor && (_.merge(shape.style, {backgroundColor: 'transparent'}))
            !shape.style?.strokeWidth && (_.merge(shape.style, {strokeWidth: DEFAULT_STROKE_WIDTH}))
          })
        })
      }, {name: 'Cleanup invalid shapes'}),
      autorun(() => {
        const {appState: {rendererBounds}, container, tab: {tabSet, indexInParent, isSelected}} = this
        container && this._debounceUpdateClientRect()
      }),
      autorun(() => {
        const {viewport} = camera
        runInAction(() => {
          if (tempViewport) {
            camera.previousViewport = _.cloneDeep(tempViewport)
            tempViewport = null
          } else {
            tempViewport = _.cloneDeep(camera.viewport)
          }
        })
      }, {name: `Save off prior viewport on change`}),
      // autorun(() => {
      //   const {appState: {rendererBounds}} = this
      //   runInAction(() => {
      //     const {camera: {initialized, viewport, previousViewport}} = this
      //     if (!initialized || !previousViewport) return
      //
      //     const widthChange = Math.abs(previousViewport.width - viewport.width) / viewport.width,
      //       heightChange = Math.abs(previousViewport.height - viewport.height) / viewport.height,
      //       largeChange = widthChange >= .25 || heightChange >= .25
      //
      //       const z = 1 / rendererBounds.width / viewport.width
      //     const z1 = 1 / viewport.width / rendererBounds.width
      //
      //     // If our renderer got significantly small (say due to splitting a tabbed view or restoring from maximized view) then zoom out to keep the same items in view as before
      //     // otherwise pan so that we keep the same center point as before
      //     console.log(`TODO Renderer bounds changed - try to recover viewport by zooming/panning!`, toJS({largeChange, widthChange, heightChange, z, z1, rendererBounds, viewport, previousViewport}, {recurseEverything: true}))
      //   })
      // }, {name: `Maintain same viewport when renderer bounds change`}),
      autorun(() => {
        const {isInSession} = this
        runInAction(() => {
          this.tab.ignoreSettingUpdates = isInSession
        })
      }, {name: `Turn off diffs while in tool`}),
      autorun(() => {
        const {pagesInOrder} = this
        runInAction(() => {
          pagesInOrder.forEach((p, i) => {
            p.childIndex = i
          })
        })
      }, {name: `Make sure page orders are correct`}),
      autorun(() => {
        const {settings: {pages}} = this
        if (_.isEmpty(pages)) {
          this.createNewPage()
        }
      }, {name: `Create page if none exists`}),
      autorun(() => {
        const {_combinedPageState} = this
        runInAction(() => {
          _.assign(this.pageState, _combinedPageState)
        })
      }),
      autorun(() => {
        const {isInSession, tab: {ignoreSettingUpdates}} = this
        if (!isInSession && !ignoreSettingUpdates) {
          const {serializableShapes, page: {bindings}} = this
          const snapshot = _.cloneDeep({shapes: _.keyBy(serializableShapes, s => s.id), bindings})
          runInAction(() => {
            const {history, undoIndex, isInUndo} = this,
              historyItem = _.isEmpty(history) ? {shapes: {}, bindings: {}} : !isInUndo ? _.last(history) : history[undoIndex]

            const diff = jsondiff.diff(historyItem, snapshot)

            // console.log(`Undo/Redo Detection - `, {diff, isInUndo, snapshot})
            if (diff || isInUndo) {
              if (isInUndo) {
                this.history = _.take(history, undoIndex)
                this.undoIndex = null
                // console.log(`No longer in undo`, {undoIndex})
              }

              this.history.push(snapshot as any)

              this.tab.throttleUpdateFlexNodeCustomSettings()
            }
          })
        }
      }, {name: 'Detect changes and save into undo list'}),
      autorun(() => {
        const {appState, appState: {hoveredId}} = this
        if (hoveredId) {
          const {page} = this
          if (!page.shapes[hoveredId]) {
            appState.hoveredId = undefined
          }
        }
      }, {name: `Clear invalid hovered id`}),
      autorun(() => {
        const {settings: {pages, ui, ui: {currentPageId}}} = this
        if (!pages[currentPageId]) {
          ui.currentPageId = undefined
        }
      }, {name: `Clear invalid current page id`}),
      // selection.autorun_manageMultiSelectTitleShape(),
      //selection.autorun_manageSelectionToolbar(),
      autorun(() => {
        if (!globalStores.dragDrop.dragItem && !_.isEmpty(this.dragCreatedShapes)) {
          this.deleteShapes(this.dragCreatedShapes.map(s => s.id))
        }
      }, {name: `Delete drag drop shapes if user cancels`})
    ]

    return () => {
      _toDispose.forEach(d => d())
    }
  }

  @computed get currentPageWrapper() {
    return this.pageWrapperById[this.currentPageId]
  }

  get shapesByType() {
    return this.currentPageWrapper?.shapesByType
  }

  @computed get selectedShapesByType() {
    return _.groupBy(this.selectedShapes, s => s.type)
  }

  @observable private _previousViewport

  get store() { return this.props.tabStore}

  @computed get selectedPageInfo(): { page: TLPage<WhiteboardShape, TDBinding>, state: WhiteboardPageState } {
    const {store: {tabSettings: {pages, ui: {currentPageId}}}} = this
    return _.isEmpty(pages) ? null : currentPageId ? pages[currentPageId] : _.first(_.values(pages))
  }

  @computed get page(): TLPage<WhiteboardShape, TDBinding> {
    return this.selectedPageInfo?.page ?? {id: 'not-found', shapes: {}, bindings: {}, childIndex: -1}
  }

  get myPageState() {
    return this.selectedPageInfo?.state ?? {history: [], camera: {point: [0, 0], zoom: 1}}
  }

  @computed get _combinedPageState(): TLPageState {
    const {currentPageId, selectedPageInfo, appState, page: {shapes}} = this
    let r = {
      ...selectedPageInfo?.state,
      id: currentPageId,
      selectedIds: (selectedPageInfo?.state?.selectedIds ?? []).filter(v => shapes[v])
    }

    return r
  }

  @computed get shapes(): Array<WhiteboardShape> {
    return this.currentPageWrapper.shapes
  }

  @computed get nextShapeIndex() {
    return (_.last(this.shapesInOrder)?.childIndex ?? 0) + 1
  }

  @computed get currentPageId() { return this.settings.ui.currentPageId}

  set currentPageId(id) { this.settings.ui.currentPageId = id}

  get meta() { return this.store.tabSettings.meta}

  get selectedIds() { return this.currentPageWrapper?.selectedIds ?? []}

  @observable mouseLocationRaw: { x: number, y: number }

  @computed get mouseLocationVirtual() {
    const {mouseLocationRaw, domContainerRect} = this
    if (!mouseLocationRaw || !domContainerRect) return null

    const {x, y} = mouseLocationRaw
    return this.getPagePoint([x - domContainerRect.left, y - domContainerRect.top])
  }

  @computed get mouseLocationVirtualRelative() {
    const {mouseLocationVirtual} = this

    if (!mouseLocationVirtual) return null

    const [x, y] = mouseLocationVirtual,
      {camera: {viewport}} = this
    return [x - viewport.minX, y - viewport.minY]
  }

  @computed get selectedIdsMap() {
    return new Set<string>(this.selectedIds)
  }

  get events() {
    const {
      onDragShape,
      onHoverShape,
      onShapeChange,
      onPointCanvas,
      onPointShape,
      onUnhoverShape,
      onDoubleClickBounds,
      onDoubleClickBoundsHandle,
      onDoubleClickCanvas,
      onDoubleClickHandle,
      onDoubleClickShape,
      onDragBounds,
      onDragBoundsHandle,
      onDragCanvas,
      onDragHandle,
      onHoverBounds,
      onHoverBoundsHandle,
      onHoverHandle,
      onZoom,
      onUnhoverHandle,
      onUnhoverBoundsHandle,
      onUnhoverBounds,
      onShapeBlur,
      onShapeClone, camera: {updateBounds},
      onReleaseBoundsHandle,
      onRightPointHandle,
      onRightPointBoundsHandle,
      onRightPointCanvas, onKeyUp, onKeyDown,
      onPan, onPinch, onPinchEnd, onPinchStart, onPointBoundsHandle, onPointBounds, onPointerUp, onPointerMove, onPointerDown, onReleaseCanvas, onReleaseBounds, onReleaseHandle, onReleaseShape, onRenderCountChange, onRightPointBounds, onRightPointShape, onPointHandle
    } = this
    return {
      onDragShape, onHoverShape, onPointCanvas, onPointShape, onUnhoverShape,
      onDoubleClickBounds, onDoubleClickBoundsHandle, onDoubleClickCanvas, onDoubleClickHandle, onDoubleClickShape, onDragBounds, onDragBoundsHandle, onDragCanvas, onDragHandle, onHoverBounds, onHoverBoundsHandle, onHoverHandle, onZoom, onUnhoverHandle, onUnhoverBoundsHandle, onUnhoverBounds,
      onShapeBlur, onShapeChange, onBoundsChange: updateBounds, onKeyUp, onKeyDown,
      onShapeClone, onReleaseBoundsHandle, onRightPointHandle, onRightPointBoundsHandle,
      onRightPointCanvas, onPan, onPinch, onPinchEnd, onPinchStart, onPointBoundsHandle, onPointBounds, onPointerUp, onPointerMove, onPointerDown, onReleaseCanvas, onReleaseBounds, onReleaseHandle, onReleaseShape, onRenderCountChange, onRightPointBounds, onRightPointShape, onPointHandle
    }
  }

  @computed get isInSession() {
    return this.tools.session !== undefined || this.appState.status === 'creating'
  }

  @computed get isSelecting() {
    return this.appState.activeTool === 'select'
  }

  @computed get selectedBounds() {
    const {selectedShapes, hasSelection, shapeUtilFor} = this
    return !hasSelection ? null : Utils.getCommonBounds(selectedShapes.filter(s => shapeUtilFor(s)).map(s => shapeUtilFor(s).getBounds(s)))
  }

  @computed get isSelectionWithinViewport() {
    const {ui: {viewport}, selectedBounds} = this
    return !selectedBounds ? false : Utils.boundsContain(viewport, selectedBounds)
  }

  @computed get isHideBoundsShape() {
    const {shapeUtilFor, selectedShapes} = this
    return selectedShapes.length === 1 &&
           shapeUtilFor(_.first(selectedShapes).type)?.hideBounds === true
  }

  @computed get isHideResizeHandlesShape() {
    const {selectedShapes, shapeUtilFor} = this
    return selectedShapes.length === 1 &&
           shapeUtilFor(_.first(selectedShapes).type)?.hideResizeHandles === true
  }

  @computed get hideBounds() {
    const {isInSession, isHideBoundsShape, isSelecting, appState: {editingId}, tools: {session}} = this
    return (isInSession && session?.constructor.name !== 'BrushSession') ||
           !isSelecting ||
           isHideBoundsShape ||
           !!editingId
  }

  @computed get hideHandles() {
    const {isInSession, isSelecting} = this
    return isInSession || !isSelecting
  }

  @computed get hideIndicators() {
    const {isInSession, appState: {status}, isSelecting} = this
    return (isInSession && status !== 'brushing') || !isSelecting
  }

  @computed get theme() {
    return this.settings.ui.isDarkMode ? 'dark' : 'light'
  }

  get settings(): IFlexLayoutWhiteboardTabConfigSettings {
    return this.store.tabSettings
  }

  @observable.ref container: HTMLDivElement
  @action setContainer = r => this.container = r

  @action handleMenuBlur: React.FocusEventHandler = (e) => {
    const {container: elm} = this
    if (!elm) return
    if (!elm.contains(e.relatedTarget)) return
    elm.dispatchEvent(new Event('pointerdown', {bubbles: true}))
    elm.dispatchEvent(new Event('pointerup', {bubbles: true}))
  }

  @observable showMenu = true
  @observable showPages = true
  @observable showStyles = true
  @observable showZoom = true

  @action onDocumentPaste = async (e: ClipboardEvent) => {
    const items = e.clipboardData.items

    const imageFiles = _.map(items, item => {
      if (item.kind === 'file') {
        return item.getAsFile()
      }
    }).filter(_.identity)

    const {myPageState, createUtil: {createImageShape}, catalog: {img: {uploadImagesToC8}}, page: {shapes}, currentPageId: parentId} = this

    myPageState.selectedIds = []
    if (!_.isEmpty(imageFiles)) {
      for (var file of imageFiles) {
        await createImageShape({file})
      }
    } else {
      this.clipboard.paste()
    }
  }

  @action toggleToolLock = () => {

  }

  /**
   * Change the style for one or more shapes.
   * @param style A style partial to apply to the shapes.
   * @param ids The ids of the shapes to change (defaults to selection).
   */
  @action applyStyle = (style: Partial<ShapeStyles>, ids = this.selectedIds) => {
    //console.log(`applyStyle`, toJS({style, ids}, {recurseEverything: true}))

    const {ui, myPageState, currentPageId, appState, getDocumentBranch, getShape, shapeUtilFor} = this,
      shapeIdsToMutate = ids
        .flatMap((id) => getDocumentBranch(id, currentPageId))
        .filter((id) => !getShape(id).isLocked)

    shapeIdsToMutate
      .map((id) => getShape(id))
      .filter((shape) => !shape.isLocked)
      .forEach((shape) => {
        _.merge(shape.style, style)

        if (shape.type === 'text') {
          const {getCenter} = shapeUtilFor(shape)

          shape.point = Vec.toFixed(
            Vec.add(
              shape.point,
              Vec.sub(
                getCenter(shape),
                getCenter(shape)
                // {
                //         ...shape,
                //         style: {...shape.style, ...shape}
                //       })
              )
            )
          )
        }
      })

    myPageState.selectedIds = ids
    _.merge(ui.currentStyle, style)
  }

  /**
   * Set or clear the editing id
   * @param id [string]
   */
  @action setEditingId = (id?: string) => {
    if (this.readOnly) return

    this.editingStartTime = Date.now()
    this.appState.editingId = id
  }

  @action onClear = () => {
    this.page.shapes = {}
  }

  get ui() { return this.settings.ui }

  @action toggleBindingHandles = () => this.ui.showBindingHandles = !this.ui.showBindingHandles
  @action toggleRotateHandles = () => this.ui.showRotateHandles = !this.ui.showRotateHandles
  @action toggleCloneHandles = () => this.ui.showCloneHandles = !this.ui.showCloneHandles
  @action toggleShowSnaps = () => this.ui.isSnapping = !this.ui.isSnapping
  @action toggleShowGrid = () => this.ui.showGrid = !this.ui.showGrid
  @action toggleDarkMode = () => this.ui.isDarkMode = !this.ui.isDarkMode
  @action toggleFocusMode = () => this.ui.isFocusMode = !this.ui.isFocusMode
  @action toggleDebugMode = () => this.ui.isDebugMode = !this.ui.isDebugMode

  @computed get currentPageName() {
    const {page} = this
    return page?.name ?? `Page ${(page?.childIndex ?? 0) + 1}`
  }

  @action createNewPage = () => {
    const {settings, settings: {pages}} = this,
      page = {
        id: guid(),
        shapes: {},
        childIndex: _.size(pages) + 1,
        bindings: {}
      }

    pages[page.id] = {
      page,
      state: {
        history: [],
        camera: {
          point: [0, 0],
          zoom: 1
        }
      }
    }
    this.selectPage(page)

    return page
  }

  @computed get pageWrapperById(): { [pageId: string]: WhiteboardPageStore } {
    return _.keyBy(this.pagesInOrder.map(p => new WhiteboardPageStore(p, this)), p => p.page.id)
  }

  @computed get pageById(): { [id: string]: WhiteboardPageDataEntryState } {
    return _.keyBy(this.settings.pages, p => p.page.id)
  }

  @computed get pagesInOrder(): Array<TLPage<WhiteboardShape>> {
    return _.orderBy(_.values(this.settings.pages).map(p => p.page), (p: TLPage<WhiteboardShape>) => p.childIndex)
  }

  @computed get nextPageIndex() {
    return (_.last(this.pagesInOrder)?.childIndex ?? 0) + 1
  }

  @action selectPage = (page: TLPage<WhiteboardShape>) => {
    this.settings.ui.currentPageId = page?.id
  }

  @action selectTool = (tool: TDToolType) => {
    this.tools.selectTool(tool)
  }

  /// region Renderer Events

  get currentTool() {
    return this.tools.currentTool
  }

  @action onPinchStart: TLPinchEventHandler = (info, e) => this.currentTool.onPinchStart?.(info, e)

  @action onPinchEnd: TLPinchEventHandler = (info, e) => this.currentTool.onPinchEnd?.(info, e)

  @action onPinch: TLPinchEventHandler = (info, e) => this.currentTool.onPinch?.(info, e)

  // @ts-ignore
  @observable pageState: TLPageState & WhiteboardAppState = {
    isStyleOpen: false,
    status: 'idle',
    snapLines: observable.array([]),
    pointedId: undefined,
    bindingId: undefined,
    hoveredId: undefined,
    activeTool: 'select',
    isToolLocked: true,
    showZoomExpandedMenu: false,
    rendererBounds: Utils.getBoundsFromPoints([
      [0, 0],
      [100, 100]
    ]),
    toolSettings: {
      draw: {
        //size: 1 + strokeWidth * 1.5,
        thinning: 0.65,
        streamline: 0.65,
        smoothing: 0.65,
      }
    }
  }

  get appState() : TLPageState & WhiteboardAppState {
    return this.pageState
  }

  @action onPan: TLWheelEventHandler = (info, e) => {
    const {appState, myPageState: {camera}} = this
    if (appState.status === 'pinching') return
    // TODO: Pan and pinchzoom are firing at the same time. Considering turning one of them off!

    const delta = Vec.div(info.delta, camera.zoom)
    const prev = camera.point
    const next = Vec.sub(prev, delta)

    if (Vec.isEqual(next, prev)) return

    this.camera.pan(delta)

    // onPan is called by onPointerMove when spaceKey is pressed,
    // so we shouldn't call this again.
    if (!info.spaceKey) {
      this.onPointerMove(info, e as unknown as React.PointerEvent)
    }
  }

  @action onZoom: TLWheelEventHandler = (info, e) => {
    if (this.appState.status !== 'idle') return

    const delta =
      e.deltaMode === WheelEvent.DOM_DELTA_PIXEL
      ? info.delta[2] / 500
      : e.deltaMode === WheelEvent.DOM_DELTA_LINE
        ? info.delta[2] / 100
        : info.delta[2] / 2

    this.camera.zoomBy(delta, info.delta)
    this.onPointerMove(info, e as unknown as React.PointerEvent)
  }

  @observable originPoint = [0, 0]

  @observable currentPoint = [0, 0]

  @observable previousPoint = [0, 0]

  @observable shiftKey = false

  @observable altKey = false

  @observable metaKey = false

  @observable ctrlKey = false

  @observable spaceKey = false

  shapeUtilFor = <T extends TDShape>(shape: T | T['type']) => {
    if (!shape) {
      debugger
      throw new Error(`Shape cannot be null`)
    }

    const {shapeUtils} = this
    if (typeof shape === 'string') return shapeUtils[shape] as unknown as TDShapeUtil<T>
    return shapeUtils[shape.type] as unknown as TDShapeUtil<T>
  }

  /**
   * Turn a screen point into a point on the page.
   * @param point The screen point
   * @param pageId (optional) The page to use
   */
  getPagePoint = (point: number[], pageId = this.currentPageId): number[] => {
    const {camera} = this.settings.pages[pageId]?.state ?? {camera: {zoom: 1, point: [0, 0]}}
    return Vec.sub(Vec.div(point, camera.zoom), camera.point)
  }

  // (x - domX) / z + camera.point.x = x1
  // (x1 - camera.point.x) * z + domX

  // [422, 448] -> [-78, -800]
  getScreenPoint = (point: number[]) => {
    const {camera} = this.myPageState ?? {camera: {zoom: 1, point: [0, 0]}},
      {domContainerRect} = this

    if (!domContainerRect) return camera.point

    return Vec.add(Vec.mul(Vec.add(point, camera.point), camera.zoom), [domContainerRect.x, domContainerRect.y])
  }

  @computed get selectedBoundsScreen() {
    const {getScreenPoint, selectedBounds, domContainerRect} = this
    if (!selectedBounds || !domContainerRect) {return null}

    const [minX, minY] = getScreenPoint([selectedBounds.minX, selectedBounds.minY]),
      [maxX, maxY] = getScreenPoint([selectedBounds.maxX, selectedBounds.maxY])

    return {
      offsetX: domContainerRect.x,
      offsetY: domContainerRect.y,
      selectionLeft: minX,
      selectionTop: minY,
      selectionBottom: maxY,
      selectionRight: maxX
    }
  }

  @computed get selectionBoundsCssStyles() {
    const {selectedBoundsScreen} = this
    if (!selectedBoundsScreen) {return null}

    const {offsetX, offsetY, selectionBottom, selectionLeft, selectionTop, selectionRight} = selectedBoundsScreen
    return {
      '--wb-offset-x': `${offsetX}px`,
      '--wb-offset-y': `${offsetY}px`,
      '--selection-left': `${selectionLeft}px`,
      '--selection-top': `${selectionTop}px`,
      '--selection-bottom': `${selectionBottom}px`,
      '--selection-right': `${selectionRight}px`
    } as any
  }

  screenToCanvas = (x: number, y: number) => {
    const {myPageState: {camera: {point, zoom}}} = this
    return [x / zoom - point[0], y / zoom - point[1]]
  }

  canvasToScreen(x: number, y: number) {
    const {myPageState: {camera: {point, zoom}}} = this
    return [x / zoom - point[0], y / zoom - point[1]]
  }

  /* ----------------- Pointer Events ----------------- */

  @action updateInputs: TLPointerEventHandler = (info) => {
    this.currentPoint = [...this.getPagePoint(info.point), info.pressure]
    this.shiftKey = info.shiftKey
    this.altKey = info.altKey
    this.ctrlKey = info.ctrlKey
    this.metaKey = info.metaKey
  }

  @action onPointerMove: TLPointerEventHandler = (info, e) => {
    this.previousPoint = this.currentPoint
    this.updateInputs(info, e)

    // Several events (e.g. pan) can trigger the same "pointer move" behavior
    this.currentTool.onPointerMove?.(info, e)
  }

  @action onPointerDown: TLPointerEventHandler = (info, e) => {
    this.originPoint = this.getPagePoint(info.point)
    this.updateInputs(info, e)
    this.currentTool.onPointerDown?.(info, e)
  }

  @action onPointerUp: TLPointerEventHandler = (info, e) => {
    this.updateInputs(info, e)
    this.currentTool.onPointerUp?.(info, e)
  }

  // Canvas (background)
  @action onPointCanvas: TLCanvasEventHandler = (info, e) => {
    this.updateInputs(info, e)
    this.currentTool.onPointCanvas?.(info, e)
  }

  @action onDoubleClickCanvas: TLCanvasEventHandler = (info, e) => {
    this.updateInputs(info, e)
    this.currentTool.onDoubleClickCanvas?.(info, e)
  }

  @action onRightPointCanvas: TLCanvasEventHandler = (info, e) => {
    this.updateInputs(info, e)
    this.currentTool.onRightPointCanvas?.(info, e)
  }

  @action onDragCanvas: TLCanvasEventHandler = (info, e) => {
    this.updateInputs(info, e)
    this.currentTool.onDragCanvas?.(info, e)
  }

  @action onReleaseCanvas: TLCanvasEventHandler = (info, e) => {
    this.updateInputs(info, e)
    this.currentTool.onReleaseCanvas?.(info, e)
  }

  // Shape
  @action onPointShape: TLPointerEventHandler = (info, e) => {
    this.appState.showZoomExpandedMenu = false
    this.originPoint = this.getPagePoint(info.point)
    this.updateInputs(info, e)
    this.currentTool.onPointShape?.(info, e)
  }

  @action onReleaseShape: TLPointerEventHandler = (info, e) => {
    this.updateInputs(info, e)
    this.currentTool.onReleaseShape?.(info, e)
  }

  @action onDoubleClickShape: TLPointerEventHandler = (info, e) => {
    this.originPoint = this.getPagePoint(info.point)
    this.updateInputs(info, e)
    this.currentTool.onDoubleClickShape?.(info, e)

    const {shapeComponentStores, getShape} = this,
      store = shapeComponentStores[info.target]

    if (store instanceof CatalogItemShapeStore) {
      store.card.openCatalogItemDetails(e)
    }
  }

  @action onRightPointShape: TLPointerEventHandler = (info, e) => {
    this.originPoint = this.getPagePoint(info.point)
    this.updateInputs(info, e)
    this.currentTool.onRightPointShape?.(info, e)
  }

  @action onDragShape: TLPointerEventHandler = (info, e) => {
    this.updateInputs(info, e)
    this.currentTool.onDragShape?.(info, e)
  }

  @action onHoverShape: TLPointerEventHandler = (info, e) => {
    this.updateInputs(info, e)
    this.currentTool.onHoverShape?.(info, e)
  }

  @action onUnhoverShape: TLPointerEventHandler = (info, e) => {
    this.updateInputs(info, e)
    this.currentTool.onUnhoverShape?.(info, e)
  }

  // Bounds (bounding box background)
  @action onPointBounds: TLBoundsEventHandler = (info, e) => {
    this.originPoint = this.getPagePoint(info.point)
    this.updateInputs(info, e)
    this.currentTool.onPointBounds?.(info, e)
  }

  @action onDoubleClickBounds: TLBoundsEventHandler = (info, e) => {
    this.originPoint = this.getPagePoint(info.point)
    this.updateInputs(info, e)
    this.currentTool.onDoubleClickBounds?.(info, e)
  }

  @action onRightPointBounds: TLBoundsEventHandler = (info, e) => {
    this.originPoint = this.getPagePoint(info.point)
    this.updateInputs(info, e)
    this.currentTool.onRightPointBounds?.(info, e)
  }

  @action onDragBounds: TLBoundsEventHandler = (info, e) => {
    this.updateInputs(info, e)
    this.currentTool.onDragBounds?.(info, e)
  }

  @action onHoverBounds: TLBoundsEventHandler = (info, e) => {
    this.updateInputs(info, e)
    this.currentTool.onHoverBounds?.(info, e)
  }

  @action onUnhoverBounds: TLBoundsEventHandler = (info, e) => {
    this.updateInputs(info, e)
    this.currentTool.onUnhoverBounds?.(info, e)
  }

  @action onReleaseBounds: TLBoundsEventHandler = (info, e) => {
    this.updateInputs(info, e)
    this.currentTool.onReleaseBounds?.(info, e)
  }

  // Bounds handles (corners, edges)
  @action onPointBoundsHandle: TLBoundsHandleEventHandler = (info, e) => {
    this.originPoint = this.getPagePoint(info.point)
    this.updateInputs(info, e)
    this.currentTool.onPointBoundsHandle?.(info, e)
  }

  @action onDoubleClickBoundsHandle: TLBoundsHandleEventHandler = (info, e) => {
    this.originPoint = this.getPagePoint(info.point)
    this.updateInputs(info, e)
    this.currentTool.onDoubleClickBoundsHandle?.(info, e)
  }

  @action onRightPointBoundsHandle: TLBoundsHandleEventHandler = (info, e) => {
    this.originPoint = this.getPagePoint(info.point)
    this.updateInputs(info, e)
    this.currentTool.onRightPointBoundsHandle?.(info, e)
  }

  @action onDragBoundsHandle: TLBoundsHandleEventHandler = (info, e) => {
    this.updateInputs(info, e)
    this.currentTool.onDragBoundsHandle?.(info, e)
  }

  @action onHoverBoundsHandle: TLBoundsHandleEventHandler = (info, e) => {
    this.updateInputs(info, e)
    this.currentTool.onHoverBoundsHandle?.(info, e)
  }

  @action onUnhoverBoundsHandle: TLBoundsHandleEventHandler = (info, e) => {
    this.updateInputs(info, e)
    this.currentTool.onUnhoverBoundsHandle?.(info, e)
  }

  @action onReleaseBoundsHandle: TLBoundsHandleEventHandler = (info, e) => {
    this.updateInputs(info, e)
    this.currentTool.onReleaseBoundsHandle?.(info, e)
  }

  // Handles (ie the handles of a selected arrow)
  @action onPointHandle: TLPointerEventHandler = (info, e) => {
    this.originPoint = this.getPagePoint(info.point)
    this.updateInputs(info, e)
    this.currentTool.onPointHandle?.(info, e)
  }

  @action onDoubleClickHandle: TLPointerEventHandler = (info, e) => {
    this.originPoint = this.getPagePoint(info.point)
    this.updateInputs(info, e)
    this.currentTool.onDoubleClickHandle?.(info, e)
  }

  @action onRightPointHandle: TLPointerEventHandler = (info, e) => {
    this.originPoint = this.getPagePoint(info.point)
    this.updateInputs(info, e)
    this.currentTool.onRightPointHandle?.(info, e)
  }

  @action onDragHandle: TLPointerEventHandler = (info, e) => {
    this.updateInputs(info, e)
    this.currentTool.onDragHandle?.(info, e)
  }

  @action onHoverHandle: TLPointerEventHandler = (info, e) => {
    this.updateInputs(info, e)
    this.currentTool.onHoverHandle?.(info, e)
  }

  @action onUnhoverHandle: TLPointerEventHandler = (info, e) => {
    this.updateInputs(info, e)
    this.currentTool.onUnhoverHandle?.(info, e)
  }

  @action onReleaseHandle: TLPointerEventHandler = (info, e) => {
    this.updateInputs(info, e)
    this.currentTool.onReleaseHandle?.(info, e)
  }

  /* ----------------- Keyboard Events ---------------- */

  @action onKeyDown: TLKeyboardEventHandler = (key, info, e) => {
    switch (e.key) {
      case 'Escape': {
        this.cancel()
        break
      }
      case 'Meta': {
        this.metaKey = true
        break
      }
      case 'Alt': {
        this.altKey = true
        break
      }
      case 'Control': {
        this.ctrlKey = true
        break
      }
      case ' ': {
        this.spaceKey = true
        break
      }
    }

    this.currentTool.onKeyDown?.(key, info, e)

    return this
  }

  @action onKeyUp: TLKeyboardEventHandler = (key, info, e) => {
    if (!info) return

    switch (e.key) {
      case 'Meta': {
        this.metaKey = false
        break
      }
      case 'Alt': {
        this.altKey = false
        break
      }
      case 'Control': {
        this.ctrlKey = false
        break
      }
      case ' ': {
        this.spaceKey = false
        break
      }
    }

    this.currentTool.onKeyUp?.(key, info, e)
  }

  @action onShapeChange = ({id, ...rest}: { id: string } & Partial<TDShape>) => {
    _.merge(this.page.shapes, {[id]: rest})
  }

  @observable editingStartTime
  @action onShapeBlur = () => {
    // This prevents an auto-blur event from Safari
    if (Date.now() - this.editingStartTime < 100) {
      console.debug(`ignore blur on edit start`)
      return
    }

    const {appState, selection: {select}, appState: {editingId, isToolLocked}, page: {shapes}} = this

    // if (editingId) {
    //   // If we're editing text, then delete the text if it's empty
    //   const shape = shapes[editingId]
    //   appState.editingId = undefined
    //   if (shape.type === 'text') {
    //     if (shape.text.trim().length <= 0) {
    //       delete shapes[editingId]
    //     } else if (!isToolLocked) {
    //       select(editingId)
    //     }
    //   }
    // }

    this.currentTool.onShapeBlur?.()
  }

  @action onShapeClone: TLShapeCloneHandler = (info, e) => this.currentTool.onShapeClone?.(info, e)

  @observable.ref shapesOnScreen: Array<WhiteboardShape> = []

  @action onRenderCountChange = (ids: string[]) => {

  }

  /// endregion

  /// region

  /**
   * Cancel the current session.
   */
  @action cancel = () => {
    this.currentTool.onCancel?.()
    return this
  }

  /// endregion

  getShape = <T extends WhiteboardShape>(id: string, pageId: string = this.currentPageId): T => {
    if (!pageId) {
      return this.page.shapes[id] as T
    } else {
      return (this.settings.pages[pageId]?.page?.shapes ?? {})[id] as T
    }
  }

  @computed get readOnly() { return false}

  getDocumentBranch = (id: string, pageId: string = this.currentPageId): string[] => {
    return this._getDocumentBranch(id, pageId)
  }

  private _getDocumentBranch = computedFn((id: string, pageId: string): string[] => {
    const shape = this.getShape(id, pageId)

    if (shape.children === undefined) return [id]

    return [
      id,
      ...shape.children.flatMap((childId) => this.getDocumentBranch(childId, pageId))
    ]
  })

  @computed get arrows(): Array<ArrowShape> {
    const {shapes} = this
    return shapes.filter(s => s.type === 'arrow') as Array<ArrowShape>
  }

  /**
   *
   */
  getLinkedShapeIds = (direction: TLBoundsHandle, includeArrows: boolean = true) => this._getLinkedShapeIds(direction, includeArrows)
  private _getLinkedShapeIds = computedFn((direction: TLBoundsHandle, includeArrows: boolean) => {
    const {myPageState: {selectedIds}, page} = this,
      linkedIds = new Set<string>(selectedIds),
      checkedIds = new Set<string>(),
      idsToCheck = [...selectedIds]

    const arrows = new Set(
      Object.values(page.shapes).filter((shape) => {
        return (
          shape.type === 'arrow' &&
          (shape.handles.start.bindingId || shape.handles?.end.bindingId)
        )
      }) as ArrowShape[]
    )

    while (idsToCheck.length) {
      const id = idsToCheck.pop()

      if (!(id && arrows.size)) break

      if (checkedIds.has(id)) continue

      checkedIds.add(id)

      arrows.forEach((arrow) => {
        const {
          handles: {
            start: {bindingId: startBindingId},
            end: {bindingId: endBindingId}
          }
        } = arrow

        const startBinding = startBindingId ? page.bindings[startBindingId] : null
        const endBinding = endBindingId ? page.bindings[endBindingId] : null

        let hit = false

        if (startBinding && startBinding.toId === id) {
          if (direction === 'center') {
            hit = true
          } else if (arrow.decorations?.start && endBinding) {
            // The arrow is pointing to this shape at its start
            hit = direction === 'left'
          } else {
            // The arrow is pointing away from this shape
            hit = direction === 'right'
          }

          if (hit) {
            // This arrow is bound to this shape
            if (includeArrows) linkedIds.add(arrow.id)
            linkedIds.add(id)

            if (endBinding) {
              linkedIds.add(endBinding.toId)
              idsToCheck.push(endBinding.toId)
            }
          }
        } else if (endBinding && endBinding.toId === id) {
          // This arrow is bound to this shape at its end
          if (direction === 'center') {
            hit = true
          } else if (arrow.decorations?.end && startBinding) {
            // The arrow is pointing to this shape
            hit = direction === 'left'
          } else {
            // The arrow is pointing away from this shape
            hit = direction === 'right'
          }

          if (hit) {
            if (includeArrows) linkedIds.add(arrow.id)
            linkedIds.add(id)

            if (startBinding) {
              linkedIds.add(startBinding.toId)
              idsToCheck.push(startBinding.toId)
            }
          }
        }

        if (
          (!startBinding || linkedIds.has(startBinding.toId)) &&
          (!endBinding || linkedIds.has(endBinding.toId))
        ) {
          arrows.delete(arrow)
        }
      })
    }

    return Array.from(linkedIds.values())
  })

  /// region Bindings

  getBinding = (id: string): TLBinding => {
    return this.page.bindings[id]
  }

  @computed get bindings(): TLBinding[] {
    const {page} = this
    return Object.values(page.bindings)
  }

  @computed get bindableShapeIds() {
    const {shapes, shapeUtilFor} = this
    return shapes
      .filter((shape) => shapeUtilFor(shape).canBind)
      .sort((a, b) => b.childIndex - a.childIndex)
      .map((shape) => shape.id)
  }

  getBindingsWithShapeIds = (ids: string[]): TLBinding[] => {
    const {bindings, page: {shapes}} = this

    return Array.from(
      new Set(
        bindings.filter((binding) => {
          return ids.includes(binding.toId) || ids.includes(binding.fromId)
        })
      ).values()
    )
  }

  getRelatedBindings = (ids: string[]): TLBinding[] => {
    const changedShapeIds = new Set(ids),
      {page, bindings} = this

    // Start with bindings that are directly bound to our changed shapes
    const bindingsToUpdate = new Set(
      bindings.filter(
        (binding) => changedShapeIds.has(binding.toId) || changedShapeIds.has(binding.fromId)
      )
    )

    // Next, look for other bindings that effect the same shapes
    let prevSize = bindingsToUpdate.size
    let delta = -1

    while (delta !== 0) {
      bindingsToUpdate.forEach((binding) => {
        const fromId = binding.fromId

        for (const otherBinding of bindings) {
          if (otherBinding.fromId === fromId) {
            bindingsToUpdate.add(otherBinding)
          }

          if (otherBinding.toId === fromId) {
            bindingsToUpdate.add(otherBinding)
          }
        }
      })

      // Continue until we stop finding new bindings to update
      delta = bindingsToUpdate.size - prevSize

      prevSize = bindingsToUpdate.size
    }

    return Array.from(bindingsToUpdate.values())
  }
  /// endregion

  @action onToggleFocus = () => {
    this.settings.ui.isFocusMode = !this.settings.ui.isFocusMode
  }

  @computed get selectedShapes(): Array<WhiteboardShape> {
    return this.currentPageWrapper.selectedShapes
    const {selectedIds, page: {shapes}} = this
    return selectedIds.map(id => shapes[id]).filter(_.identity)
  }

  @computed get selectedShapesFrame() {
    const {shapesByType: {frame}, shapeComponentStores, selectedShapes} = this
    if (_.isEmpty(frame) || _.isEmpty(selectedShapes)) return null

    return frame.find(f => {
      const store = shapeComponentStores[f.id] as FrameShapeStore
      if (!store) return false

      const {shapesContainedBySet} = store
      return selectedShapes.every(s => shapesContainedBySet.has(s.id))
    })
  }

  @computed get unlockedSelectedShapes() {
    return this.selectedShapes.filter(s => !s.isLocked)
  }

  @computed get unlockedShapes() {
    return this.shapes.filter(s => !s.isLocked)
  }

  @computed get hasShapeSelected() { return !_.isEmpty(this.selectedShapes)}

  @computed get hasMultipleShapesSelected() { return this.selectedShapes.length > 1}

  @computed get hasThreeOrMoreShapesSelected() { return this.selectedShapes.length >= 3}

  @computed get hasGroupSelected() {
    return this.selectedShapes.some(s => !_.isEmpty(s.children))
  }

  @computed get areAllSelectedShapesGrouped() {
    const {currentPageId, selectedShapes} = this
    return selectedShapes.every(
      (shape) =>
        shape.children !== undefined ||
        (shape.parentId === selectedShapes[0].parentId &&
         selectedShapes[0].parentId !== currentPageId)
    )
  }

  @computed get areAllSelectedShapesLocked() {
    const {selectedShapes} = this
    return selectedShapes.every(s => s.isLocked)
  }

  @computed get areAllSelectedShapesAspectLocked() {
    const {selectedShapes} = this
    return selectedShapes.every(s => s.isAspectRatioLocked)
  }

  @computed get selectedTextualShapes() {
    const {selectedShapes} = this
    return selectedShapes.filter(s => s.type === 'sticky' || s.type === 'text' || s.type === 'frame')
  }

  @computed get areAnyTextEnabledShapesSelected() {
    return !_.isEmpty(this.selectedTextualShapes) || this.selectedShapes.some(s => s.optionalTitle?.show)
  }

  /**
   * Update the state using a Command.
   * This effects the undo/redo stack.
   * This persists the state.
   * @param command The command to apply and add to the undo/redo stack.
   * @param id (optional) An id for this command.
   */
  @action setState = (action?) => {
    return this
    // if (this.pointer < this.stack.length - 1) {
    //     this.stack = this.stack.slice(0, this.pointer + 1)
    //   }
    //   this.stack.push({ ...command, id })
    //   this.pointer = this.stack.length - 1
    //   this.applyPatch(command.after, id)
    //   if (this.onCommand) this.onCommand(this._state, id)
    //   this.persist(id)
    //   return this
  }

  /**
   * Create one or more shapes.
   * @param shapes An array of shapes.
   * @command
   */
  @action create = (shapes: TDShape[] = [], bindings: TDBinding[] = []): this => {
    if (shapes.length === 0) return this

    const {page, myPageState} = this
    _.merge(page, {shapes: _.keyBy(shapes, s => s.id), bindings: _.keyBy(bindings, b => b.id)})
    myPageState.selectedIds = shapes.map((shape) => shape.id)
  }

  /**
   * Manually create shapes on the page.
   * @param shapes An array of shape partials, containing the initial props for the shapes.
   * @command
   */
  @action createShapes = (...shapes: ({ id: string; type: TDShapeType } & Partial<TDShape>)[]): this => {
    if (shapes.length === 0) return this

    const {currentPageId, shapeUtilFor} = this
    return this.create(
      shapes.map((shape) => {
        return shapeUtilFor(shape.type).create({
          parentId: currentPageId,
          ...shape
        })
      })
    )
  }

  /**
   * Manually update a set of shapes.
   * @param shapes An array of shape partials, containing the changes to be made to each shape.
   * @command
   */
  @action updateShapes = (...shapes: ({ id: string } & Partial<TDShape>)[]): this => {
    const {shapes: pageShapes} = this,
      shapesToUpdate = shapes.filter((shape) => pageShapes[shape.id])

    if (shapesToUpdate.length === 0) return this
    shapesToUpdate.forEach(s => pageShapes[s.id] = s)
  }

  @action patchCreate = (shapes: TDShape[] = [], bindings: TDBinding[] = []): this => {
    if (shapes.length === 0) return this
    const {page} = this
    shapes.forEach(s => page.shapes[s.id] = {...s, parentId: page.id})
  }

  /**
   * Manually patch a set of shapes.
   * @param shapes An array of shape partials, containing the changes to be made to each shape.
   * @command
   */
  @action patchShapes = (...shapes: ({ id: string } & Partial<TDShape>)[]): this => {
    const {shapes: pageShapes} = this,
      shapesToUpdate = shapes.filter((shape) => pageShapes[shape.id])

    if (shapesToUpdate.length === 0) return this
    shapesToUpdate.forEach(s => _.merge(pageShapes[s.id], s))
  }

  @action createTextShapeAtPoint = (point: number[], id?: string): this => {
    console.debug(`createTextShapeAtPoint`, {point, id})
    const {
      createShapes, nextShapeIndex: childIndex, currentPageId, setEditingId, ui: {currentStyle},
      shapeUtils: {text: {create, getBounds}}
    } = this

    const newShape = create({
      id: id || Utils.uniqueId(),
      parentId: currentPageId,
      childIndex,
      point,
      style: {...currentStyle}
    })

    const bounds = getBounds(newShape)
    newShape.point = Vec.sub(newShape.point, [bounds.width / 2, bounds.height / 2])
    createShapes(newShape)
    setEditingId(newShape.id)

    return this
  }

  get shapesInOrder(): Array<WhiteboardShape> {
    return this.currentPageWrapper.shapesInOrder
  }

  /**
   * Delete one or more shapes.
   * @param ids The ids of the shapes to delete.
   * @command
   */
  @action deleteShapes = (ids = this.selectedIds) => {
    console.debug(`Delete shapes ${ids.join(', ')}`)
    const {page: {shapes}, myPageState} = this
    ids.forEach(id => delete shapes[id])
    const {shapesInOrder} = this,
      lastSelectableShape = _.findLast(shapesInOrder, s => !s?.isGenerated)

    myPageState.selectedIds = lastSelectableShape ? [lastSelectableShape.id] : []
  }

  /**
   * Delete all shapes on the page.
   */
  @action deleteAll = () => {
    console.debug(`Delete all shapes`)
    this.undo_end()
    const {page, myPageState} = this
    page.shapes = {}
    myPageState.selectedIds = []
  }

  /**
   * Align one or more shapes.
   * @param direction Whether to align horizontally or vertically.
   * @param ids The ids of the shapes to change (defaults to selection).
   */
  @action align = (type: AlignType, ids = this.selectedIds) => {
    if (ids.length < 2) return this
    this.undo_end()

    const {currentPageId, selectedShapes, shapesByType, page, myPageState, shapeUtilFor, getShape} = this,
      boundsForShapes = selectedShapes.map((shape) => {
        return {
          id: shape.id,
          point: [...shape.point],
          bounds: shapeUtilFor(shape).getBounds(shape)
        }
      }),
      commonBounds = Utils.getCommonBounds(boundsForShapes.map(({bounds}) => bounds))

    selectedShapes.forEach(s => {
      if (s.type === 'catalogItem') {
        const card = s as CatalogItemCardShape
        switch (type) {
          case 'top':
          case 'centerVertical':
          case 'bottom': {
            s.verticalAlign = type
            break
          }

          case 'left':
          case 'centerHorizontal':
          case 'right': {
            s.horizontalAlign = type
            break
          }

          default: {
            break
          }
        }
      }
    })

    const midX = commonBounds.minX + commonBounds.width / 2,
      midY = commonBounds.minY + commonBounds.height / 2

    const deltaMap = Object.fromEntries(
      boundsForShapes.map(({id, point, bounds}) => {
        return [
          id,
          {
            prev: point,
            next: {
              ['centerVertical']: [point[0], midY - bounds.height / 2],
              ['centerHorizontal']: [midX - bounds.width / 2, point[1]],
              ['top']: [point[0], commonBounds.minY],
              ['bottom']: [point[0], commonBounds.maxY - bounds.height],
              ['left']: [commonBounds.minX, point[1]],
              ['right']: [commonBounds.maxX - bounds.width, point[1]]
            }[type]
          }
        ]
      })
    )

    const {before, after} = TLDR.mutateShapes(
      this,
      ids,
      (shape) => {
        if (!deltaMap[shape.id]) return shape
        return {point: deltaMap[shape.id].next}
      },
      currentPageId
    );

    (shapesByType['group'] ?? []).forEach((shape) => {
      const delta = Vec.sub(after[shape.id].point!, before[shape.id].point!)

      shape.children.forEach((id) => {
        const child = getShape(id)
        after[child.id] = {point: Vec.add(child.point, delta)}
      })

      delete after[shape.id]
    })

    // console.log({after, deltaMap})
    _.merge(page.shapes, after)
    myPageState.selectedIds = ids
  }

  /**
   * Distribute one or more shapes.
   * @param direction Whether to distribute horizontally or vertically..
   * @param ids The ids of the shapes to change (defaults to selection).
   */
  @action distribute = (direction: DistributeType) => {
    const {getShape, currentPageId, page, selectedIds, selectedShapes, getDistributions} = this

    if (selectedIds.length < 3) return this
    this.undo_end()

    const initialShapes = selectedShapes.slice(),
      deltaMap = Object.fromEntries(getDistributions(initialShapes, direction).map((d) => [d.id, d]))

    const {before, after} = TLDR.mutateShapes(
      this,
      selectedIds.filter((id) => deltaMap[id] !== undefined),
      (shape) => ({point: deltaMap[shape.id].next}),
      currentPageId
    )

    selectedShapes.forEach((shape) => {
      if (shape.type === 'group') {
        const delta = Vec.sub(after[shape.id].point!, before[shape.id].point!)

        shape.children.forEach((id) => {
          const child = getShape(id)
          before[child.id] = {point: child.point}
          after[child.id] = {point: Vec.add(child.point, delta)}
        })

        delete before[shape.id]
        delete after[shape.id]
      }
    })

    _.merge(page.shapes, after)
  }

  /**
   * Stretch one or more shapes to their common bounds.
   * @param direction Whether to stretch horizontally or vertically.
   * @param ids The ids of the shapes to change (defaults to selection).
   */
  @action stretch = (direction: StretchType) => {
    const {selectedShapes, getShape, currentPageId, selection: {selectedBounds: commonBounds}, shapeUtil: {getBounds, transformSingle}} = this
    if (selectedShapes.length < 2) return this
    this.undo_end()

    const initialShapes = selectedShapes.slice()

    const idsToMutate = selectedShapes
      .flatMap((shape) => {
        return shape.children ? shape.children : shape.id
      })
      .filter((id) => !getShape(id).isLocked)

    const {before, after} = TLDR.mutateShapes(
      this,
      idsToMutate,
      (shape) => {
        const bounds = getBounds(shape)

        switch (direction) {
          case 'horizontal': {
            const newBounds = {
              ...bounds,
              minX: commonBounds.minX,
              maxX: commonBounds.maxX,
              width: commonBounds.width
            }

            return transformSingle(shape, newBounds, {
              type: TLBoundsCorner.TopLeft,
              scaleX: newBounds.width / bounds.width,
              scaleY: 1,
              initialShape: shape,
              transformOrigin: [0.5, 0.5]
            })
          }
          case 'vertical': {
            const newBounds = {
              ...bounds,
              minY: commonBounds.minY,
              maxY: commonBounds.maxY,
              height: commonBounds.height
            }

            return transformSingle(shape, newBounds, {
              type: TLBoundsCorner.TopLeft,
              scaleX: 1,
              scaleY: newBounds.height / bounds.height,
              initialShape: shape,
              transformOrigin: [0.5, 0.5]
            })
          }
        }
      },
      currentPageId
    )

    initialShapes.forEach((shape) => {
      if (shape.type === 'group') {
        delete before[shape.id]
        delete after[shape.id]
      }
    })

    _.merge(this.page.shapes, after)
  }

  @computed get allShapesBounds() {
    const boundsForShapes = this.shapes.map((shape) => this.shapeUtil.getBounds(shape))

    return Utils.getCommonBounds(boundsForShapes)
  }

  @action flip = (type: FlipType) => {
    const {unlockedSelectedShapes, allShapesBounds, shapeUtil: {getBounds, transform}} = this
    this.undo_end()

    unlockedSelectedShapes.forEach((shape) => {
      const shapeBounds = getBounds(shape)

      switch (type) {
        case 'horizontal': {
          const newShapeBounds = Utils.getRelativeTransformedBoundingBox(
            allShapesBounds,
            allShapesBounds,
            shapeBounds,
            true,
            false
          )

          const r = transform(shape, newShapeBounds, {
            type: TLBoundsCorner.TopLeft,
            scaleX: -1,
            scaleY: 1,
            initialShape: shape,
            transformOrigin: [0.5, 0.5]
          })

          _.assign(shape, r)

          break
        }

        case 'vertical': {
          const newShapeBounds = Utils.getRelativeTransformedBoundingBox(
            allShapesBounds,
            allShapesBounds,
            shapeBounds,
            false,
            true
          )

          const r = transform(shape, newShapeBounds, {
            type: TLBoundsCorner.TopLeft,
            scaleX: 1,
            scaleY: -1,
            initialShape: shape,
            transformOrigin: [0.5, 0.5]
          })
          _.assign(shape, r)
          break
        }
      }
    })
  }

  /**
   * Flip one or more shapes horizontally.
   * @param ids The ids of the shapes to change (defaults to selection).
   */
  @action flipHorizontal = () => {
    this.flip('horizontal')
  }

  /**
   * Flip one or more shapes vertically.
   * @param ids The ids of the shapes to change (defaults to selection).
   */
  @action flipVertical = (ids = this.selectedIds) => {
    this.flip('vertical')
  }

  /**
   * Move one or more shapes to a new page. Will also break or move bindings.
   * @param toPageId The id of the page to move the shapes to.
   * @param fromPageId The id of the page to move the shapes from (defaults to current page).
   * @param ids The ids of the shapes to move (defaults to selection).
   */
  @action moveToPage = (
    toPageId: string,
    fromPageId = this.page.id,
    ids = this.selectedIds
  ) => {
    if (ids.length === 0) return this
    this.undo_end()
    const {appState: {rendererBounds}} = this
    this.setState(Commands.moveShapesToPage(this, ids, rendererBounds, fromPageId, toPageId))
    return this
  }

  @computed get parentsOfSelectedShapes(): Array<string> {
    const {selectedShapes} = this

    return _.uniq(selectedShapes.map(s => s.parentId))
  }

  @action reorderShapes = (type: MoveType) => {
    const {currentPageId, page, parentsOfSelectedShapes, selectedShapes, getShape} = this
    this.undo_end()

    let startIndex: number
    let startChildIndex: number
    let step: number

    // Collect shapes with common parents into a table under their parent id
    parentsOfSelectedShapes.forEach((parentId) => {
      let sortedChildren: TDShape[] = []
      if (parentId === page.id) {
        sortedChildren = Object.values(page.shapes).sort((a, b) => a.childIndex - b.childIndex)
      } else {
        const parent = getShape(parentId)
        if (!parent.children) throw Error('No children in parent!')

        sortedChildren = parent.children
          .map((childId) => getShape(childId))
          .sort((a, b) => a.childIndex - b.childIndex)
      }

      const sortedChildIds = sortedChildren.map((shape) => shape.id)

      const sortedIndicesToMove = selectedShapes
        .filter(({id}) => sortedChildIds.includes(id))
        .map(({id}) => sortedChildIds.indexOf(id))
        .sort((a, b) => a - b)

      if (sortedIndicesToMove.length === sortedChildIds.length) return

      let result: {
        before: Record<string, Partial<TDShape>>
        after: Record<string, Partial<TDShape>>
        data: TDSnapshot
      }

      switch (type) {
        case 'toBack': {
          //               a       b  c
          // Initial   1   2    3  4  5  6  7
          // Final   .25  .5  .75  1  3  6  7
          //           a   b    c

          // Find the lowest "open" index
          for (let i = 0; i < sortedChildIds.length; i++) {
            if (sortedIndicesToMove.includes(i)) continue
            startIndex = i
            break
          }

          // Find the lowest child index that isn't in sortedIndicesToMove
          startChildIndex = sortedChildren[startIndex].childIndex

          // Find the step for each additional child
          step = startChildIndex / (sortedIndicesToMove.length + 1)

          // Get the results of moving the selected shapes below the first open index's shape
          result = TLDR.mutateShapes(
            this,
            sortedIndicesToMove.map((i) => sortedChildren[i].id).reverse(),
            (_shape, i) => ({
              childIndex: startChildIndex - (i + 1) * step
            }),
            currentPageId
          )

          break
        }
        case 'toFront': {
          //              a     b  c
          // Initial   1  2  3  4  5  6   7
          // Final     1  3  6  7  8  9  10
          //                       a  b   c

          // Find the highest "open" index
          for (let i = sortedChildIds.length - 1; i >= 0; i--) {
            if (sortedIndicesToMove.includes(i)) continue
            startIndex = i
            break
          }

          // Find the lowest child index that isn't in sortedIndicesToMove
          startChildIndex = sortedChildren[startIndex].childIndex

          // Find the step for each additional child
          step = 1

          // Get the results of moving the selected shapes below the first open index's shape
          result = TLDR.mutateShapes(
            this,
            sortedIndicesToMove.map((i) => sortedChildren[i].id),
            (_shape, i) => ({
              childIndex: startChildIndex + (i + 1)
            }),
            currentPageId
          )

          break
        }
        case 'backward': {
          //               a           b  c
          // Initial    1  2     3     4  5  6  7
          // Final     .5  1  1.66  2.33  3  6  7
          //           a         b     c

          const indexMap: Record<string, number> = {}

          // Starting from the top...
          for (let i = sortedChildIds.length - 1; i >= 0; i--) {
            // If we found a moving index...
            if (sortedIndicesToMove.includes(i)) {
              for (let j = i; j >= 0; j--) {
                // iterate downward until we find an open spot
                if (!sortedIndicesToMove.includes(j)) {
                  // i = the index of the first closed spot
                  // j = the index of the first open spot

                  const endChildIndex = sortedChildren[j].childIndex
                  let startChildIndex: number
                  let step: number

                  if (j === 0) {
                    // We're moving below the first child, start from
                    // half of its child index.

                    startChildIndex = endChildIndex / 2
                    step = endChildIndex / 2 / (i - j + 1)
                  } else {
                    // Start from the child index of the child below the
                    // child above.
                    startChildIndex = sortedChildren[j - 1].childIndex
                    step = (endChildIndex - startChildIndex) / (i - j + 1)
                    startChildIndex += step
                  }

                  for (let k = 0; k < i - j; k++) {
                    indexMap[sortedChildren[j + k + 1].id] = startChildIndex + step * k
                  }

                  break
                }
              }
            }
          }

          if (Object.values(indexMap).length > 0) {
            // Get the results of moving the selected shapes below the first open index's shape
            result = TLDR.mutateShapes(
              this,
              sortedIndicesToMove.map((i) => sortedChildren[i].id),
              (shape) => ({
                childIndex: indexMap[shape.id]
              }),
              currentPageId
            )
          }

          break
        }
        case 'forward': {
          //             a     b c
          // Initial   1 2   3 4 5 6 7
          // Final     1 3 3.5 6 7 8 9
          //                 a     b c

          const indexMap: Record<string, number> = {}

          // Starting from the top...
          for (let i = 0; i < sortedChildIds.length; i++) {
            // If we found a moving index...
            if (sortedIndicesToMove.includes(i)) {
              // Search for the first open spot above this one
              for (let j = i; j < sortedChildIds.length; j++) {
                if (!sortedIndicesToMove.includes(j)) {
                  // i = the low index of the first closed spot
                  // j = the high index of the first open spot

                  startChildIndex = sortedChildren[j].childIndex

                  const step =
                    j === sortedChildIds.length - 1
                    ? 1
                    : (sortedChildren[j + 1].childIndex - startChildIndex) / (j - i + 1)

                  for (let k = 0; k < j - i; k++) {
                    indexMap[sortedChildren[i + k].id] = startChildIndex + step * (k + 1)
                  }

                  break
                }
              }
            }
          }

          if (Object.values(indexMap).length > 0) {
            // Get the results of moving the selected shapes below the first open index's shape
            result = TLDR.mutateShapes(
              this,
              sortedIndicesToMove.map((i) => sortedChildren[i].id),
              (shape) => ({
                childIndex: indexMap[shape.id]
              }),
              currentPageId
            )
          }

          break
        }
      }

      _.merge(this.page.shapes, result.after)
    })
  }

  /**
   * Move one or more shapes to the back of the page.
   * @param ids The ids of the shapes to change (defaults to selection).
   */
  @action moveToBack = (ids = this.selectedIds) => {
    if (ids.length === 0) return this
    this.reorderShapes('toBack')
  }

  /**
   * Move one or more shapes backward on of the page.
   * @param ids The ids of the shapes to change (defaults to selection).
   */
  @action moveBackward = (ids = this.selectedIds) => {
    if (ids.length === 0) return this
    this.reorderShapes('backward')
  }

  /**
   * Move one or more shapes forward on the page.
   * @param ids The ids of the shapes to change (defaults to selection).
   */
  @action moveForward = (ids = this.selectedIds) => {
    if (ids.length === 0) return this
    this.reorderShapes('forward')
  }

  /**
   * Move one or more shapes to the front of the page.
   * @param ids The ids of the shapes to change (defaults to selection).
   */
  @action moveToFront = (ids = this.selectedIds) => {
    if (ids.length === 0) return this
    this.reorderShapes('toFront')
  }

  @action translateShapes = (shapes = this.selectedShapes, delta: number[]) => {
    const {currentPageId, page, myPageState, getShape, ui} = this,
      ids = shapes.map(s => s.id)

    console.log(`translateShapes`, {ids, delta})

    // Clear session cache
    ui.rotationInfo.selectedIds = [...ids]

    const idsToMutate = shapes
      .flatMap(shape => {
        return shape.children ? shape.children : shape.id
      })
      .filter((id) => !getShape(id).isLocked)

    const change = TLDR.mutateShapes(
      this,
      idsToMutate,
      (shape) => ({
        point: Vec.toFixed(Vec.add(shape.point, delta))
      }),
      currentPageId
    )

    console.log(change)
    _.merge(page.shapes, change.after)

    // Delete bindings from nudged shapes, unless both bound and bound-to shapes are selected
    const bindingsToDelete = TLDR.getBindings(this, currentPageId).filter(
      (binding) => shapes.some(s => s.id === binding.fromId) && !shapes.some(s => s.id === binding.toId)
    )

    bindingsToDelete.forEach((binding) => {
      delete page.bindings[binding.id]

      for (const id of [binding.toId, binding.fromId]) {
        // Let's also look at the bound shape...
        const shape = getShape(id)

        if (!shape.handles) continue

        // If the bound shape has a handle that references the deleted binding, delete that reference

        Object.values(shape.handles)
          .filter((handle) => handle.bindingId === binding.id)
          .forEach((handle) => {
            handle.bindingId = undefined
          })
      }
    })

    if (shapes != this.selectedShapes) {
      myPageState.selectedIds = shapes.map(s => s.id)
    }
  }

  @computed get grid() {
    const {camera: {zoom}, ui: {gridSize, showGrid}} = this
    if (!showGrid) return null

    if (zoom < 0.15) {
      return gridSize * 16
    } else if (zoom < 1) {
      return gridSize * 4
    } else {
      return gridSize * 1
    }
  }

  /**
   * Nudge one or more shapes in a direction.
   * @param delta The direction to nudge the shapes.
   * @param isMajor Whether this is a major (i.e. shift) nudge.
   * @param ids The ids to change (defaults to selection).
   */
  @action nudge = (delta: number[], isMajor = false, shapes = this.selectedShapes) => {
    if (shapes.length === 0) return this

    const {ui: {showGrid}, grid} = this
    const size = isMajor
                 ? showGrid
                   ? grid * 4
                   : 10
                 : showGrid
                   ? grid
                   : 1

    this.translateShapes(shapes, Vec.mul(delta, size))
  }

  /**
   * Duplicate one or more shapes.
   * @param ids The ids to duplicate (defaults to selection).
   */
  @action duplicate = (point?: number[]) => {
    const {
      readOnly, selectedIds, currentPageId, page: {bindings, shapes}, myPageState,
      getShape, selectedShapes, selectedIdsMap, getChildIndexAbove, shapeUtil: {getBounds}
    } = this

    if (readOnly) return this
    if (selectedIds.length === 0) return this

    this.undo_end()

    // Remove any shapes from the list if they have a parent that we haven't selected
    const shapesToDuplicate = selectedShapes.filter(s => !selectedIdsMap.has(s.parentId))

    const duplicateMap: Record<string, string> = {}

    // Create duplicates
    shapesToDuplicate.forEach((shape) => {
      const duplicatedId = Utils.uniqueId()

      duplicateMap[shape.id] = duplicatedId
      const duplicateShape = shapes[duplicatedId] = {
        ...Utils.deepClone(shape),
        id: duplicatedId,
        childIndex: getChildIndexAbove(shape.id, currentPageId)
      }

      if (shape.parentId !== currentPageId) {
        const parent = getShape(shape.parentId)

        shapes[parent.id].children = [...(shapes[parent.id] || parent).children!, duplicatedId]
      }

      if (!_.isEmpty(shape.children)) {
        shapes[duplicatedId]!.children = []
        const parentId = duplicatedId

        shape.children.forEach((childId) => {
          const child = getShape(childId),
            duplicatedId = Utils.uniqueId()

          duplicateMap[childId] = duplicatedId

          shapes[duplicatedId] = {
            ...Utils.deepClone(child),
            id: duplicatedId,
            parentId,
            childIndex: this.getChildIndexAbove(child.id, currentPageId)
          }
          duplicateShape?.children?.push(duplicatedId)
        })
      }
    })

    // Which ids did we end up duplicating?
    const dupedShapeIds = new Set(Object.keys(duplicateMap))

    // Handle bindings that effect duplicated shapes
    Object.values(bindings)
      .filter((binding) => dupedShapeIds.has(binding.fromId) || dupedShapeIds.has(binding.toId))
      .forEach((binding: TLBinding) => {
        if (dupedShapeIds.has(binding.fromId)) {
          if (dupedShapeIds.has(binding.toId)) {
            // If the binding is between two duplicating shapes then
            // duplicate the binding, too
            const duplicatedBindingId = Utils.uniqueId()

            const duplicatedBinding = {
              ...Utils.deepClone(binding),
              id: duplicatedBindingId,
              fromId: duplicateMap[binding.fromId],
              toId: duplicateMap[binding.toId]
            }

            bindings[duplicatedBindingId] = duplicatedBinding

            // Change the duplicated shape's handle so that it reference
            // the duplicated binding
            const boundShape = shapes[duplicatedBinding.fromId]
            Object.values(boundShape!.handles!).forEach((handle: TLHandle) => {
              if (handle!.bindingId === binding.id) {
                handle!.bindingId = duplicatedBindingId
              }
            })
          } else {
            // If only the fromId is selected, delete the binding on
            // the duplicated shape's handles
            const boundShape = shapes[duplicateMap[binding.fromId]]
            Object.values(boundShape!.handles!).forEach((handle: TLHandle) => {
              if (handle!.bindingId === binding.id) {
                handle!.bindingId = undefined
              }
            })
          }
        }
      })

    // Now move the shapes

    const shapesToMove = Object.values(duplicateMap).map(id => getShape(id)) as TDShape[]

    if (point) {
      const commonBounds = Utils.getCommonBounds(shapesToMove.map((shape) => getBounds(shape)))
      const center = Utils.getBoundsCenter(commonBounds)
      shapesToMove.forEach((shape) => {
        // Could be a group
        if (!shape.point) return
        shape.point = Vec.sub(point, Vec.sub(center, shape.point))
      })
    } else {
      const offset = [16, 16]
      shapesToMove.forEach((shape) => {
        // Could be a group
        if (!shape.point) return
        shape.point = Vec.add(shape.point, offset)
      })
    }

    // Unlock any locked shapes
    shapesToMove.forEach((shape) => {
      if (shape.isLocked) {
        shape.isLocked = false
      }
    })

    myPageState.selectedIds = Array.from(dupedShapeIds.values()).map((id) => duplicateMap[id])
  }

  /**
   * Reset the bounds for one or more shapes. Usually when the
   * bounding box of a shape is double-clicked. Different shapes may
   * handle this differently.
   * @param ids The ids to change (defaults to selection).
   */
  @action resetBounds = () => {
    const {currentPageId: pageId, page, shapeUtilFor, selectedIds: ids} = this,
      {after} = TLDR.mutateShapes(
        this,
        ids,
        (shape) => shapeUtilFor(shape).onDoubleClickBoundsHandle?.(shape),
        pageId
      )

    _.merge(page.shapes, after)
  }

  /**
   * Toggle the hidden property of one or more shapes.
   * @param ids The ids to change (defaults to selection).
   */
  @action toggleHidden = (ids = this.selectedIds) => {
    if (ids.length === 0) return this
    return this.setState(Commands.toggleShapeProp(this, ids, 'isHidden'))
  }

  /**
   * Toggle the locked property of one or more shapes.
   * @param ids The ids to change (defaults to selection).
   */
  @action toggleLocked = (ids = this.selectedIds) => {
    if (ids.length === 0) return this
    return this.setState(Commands.toggleShapeProp(this, ids, 'isLocked'))
  }

  /**
   * Toggle the fixed-aspect-ratio property of one or more shapes.
   * @param ids The ids to change (defaults to selection).
   */
  @action toggleAspectRatioLocked = (ids = this.selectedIds) => {
    if (ids.length === 0) return this
    return this.setState(Commands.toggleShapeProp(this, ids, 'isAspectRatioLocked'))
  }

  /**
   * Toggle the decoration at a handle of one or more shapes.
   * @param handleId The handle to toggle.
   * @param ids The ids of the shapes to toggle the decoration on.
   */
  @action toggleDecoration = (handleId: string) => {
    const {selectedShapes, page} = this
    if (selectedShapes.length === 0 || !(handleId === 'start' || handleId === 'end')) return this

    selectedShapes.forEach((s: ArrowShape) => {
      if (!s.isLocked && s.type === 'arrow') {
        s.decorations[handleId] = s.decorations[handleId] ? undefined : Decoration.Arrow
      }
    })
  }

  /**
   * Rotate one or more shapes by a delta.
   * @param delta The delta in radians.
   * @param ids The ids to rotate (defaults to selection).
   */
  @action rotate = (delta = Math.PI * -0.5, ids = this.selectedIds) => {
    if (ids.length === 0) return this
    this.undo_end()
    const change = Commands.rotateShapes(this, ids, delta)
    if (!change) return this
    return this.setState(change)
  }
  /**
   * Group the selected shapes.
   * @param ids The ids to group (defaults to selection).
   * @param groupId The new group's id.
   */
  @action group = (
    ids = this.selectedIds,
    groupId = Utils.uniqueId(),
    pageId = this.page.id
  ) => {
    const {ungroup, readOnly, getShape, pageById, appState, shapeUtil: {getBounds}, shapeUtils: {group: groupUtil}} = this,
      {page, state} = pageById[pageId]

    if (!page || readOnly) return

    this.undo_end()
    if (ids.length === 1 && getShape(ids[0], pageId).type === 'group') {
      return ungroup(ids, pageId)
    }

    if (ids.length < 2) return

    const {bindings, shapes} = page,
      idsToGroup = [...ids],
      shapesToGroup: TDShape[] = [],
      deletedGroupIds: string[] = [],
      otherEffectedGroups: TDShape[] = []

    // Collect all of the shapes to group (and their ids)
    for (const id of ids) {
      const shape = getShape(id)
      if (shape.isLocked) continue

      if (shape.children === undefined) {
        shapesToGroup.push(shape)
      } else {
        const childIds = shape.children.filter((id) => !getShape(id).isLocked)

        otherEffectedGroups.push(shape)
        idsToGroup.push(...childIds)
        shapesToGroup.push(...childIds.map((id) => getShape(id)))
      }
    }

    // 1. Can we create this group?

    // Do the shapes have the same parent?
    if (shapesToGroup.every((shape) => shape.parentId === shapesToGroup[0].parentId)) {
      // Is the common parent a shape (not the page)?
      if (shapesToGroup[0].parentId !== pageId) {
        const commonParent = getShape(shapesToGroup[0].parentId)
        // Are all of the common parent's shapes selected?
        if (commonParent.children?.length === idsToGroup.length) {
          // Don't create a group if that group would be the same as the
          // existing group.
          return
        }
      }
    }

    // A flattened array of shapes from the page
    const flattenedShapes = this.flattenPage(pageId)

    // A map of shapes to their index in flattendShapes
    const shapeIndexMap = Object.fromEntries(
      shapesToGroup.map((shape) => [shape.id, flattenedShapes.indexOf(shape)])
    )

    // An array of shapes in order by their index in flattendShapes
    const sortedShapes = shapesToGroup.sort((a, b) => shapeIndexMap[a.id] - shapeIndexMap[b.id])

    // The parentId is always the current page
    const groupParentId = pageId // sortedShapes[0].parentId

    // The childIndex should be the lowest index of the selected shapes
    // with a parent that is the current page; or else the child index
    // of the lowest selected shape.
    const groupChildIndex = (
      sortedShapes.filter((shape) => shape.parentId === pageId)[0] || sortedShapes[0]
    ).childIndex

    // The shape's point is the min point of its childrens' common bounds
    const groupBounds = Utils.getCommonBounds(shapesToGroup.map((shape) => getBounds(shape)))

    // Create the group

    shapes[groupId] = groupUtil.create({
      id: groupId,
      childIndex: groupChildIndex,
      parentId: groupParentId,
      point: [groupBounds.minX, groupBounds.minY],
      size: [groupBounds.width, groupBounds.height],
      children: sortedShapes.map((shape) => shape.id)
    })

    // Reparent shapes to the new group
    const beforeShapes = _.keyBy(sortedShapes, s => s.id)

    sortedShapes.forEach((shape, index) => {
      // If the shape is part of a different group, mark the parent shape for cleanup
      if (shape.parentId !== pageId) {
        const parentShape = getShape(shape.parentId)
        otherEffectedGroups.push(parentShape)
      }

      _.merge(
        page.shapes[shape.id],
        {
          parentId: groupId,
          childIndex: index + 1
        })
    })

    // Clean up effected parents
    while (otherEffectedGroups.length > 0) {
      const shape = otherEffectedGroups.pop()
      if (!shape) break

      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const nextChildren = (beforeShapes[shape.id]?.children || shape.children)!.filter(
        (childId) => childId && !(idsToGroup.includes(childId) || deletedGroupIds.includes(childId))
      )

      // If the parent has no children, remove it
      if (nextChildren.length === 0) {
        delete page.shapes[shape.id]

        // And if that parent is part of a different group, mark it for cleanup
        // (This is necessary only when we implement nested groups.)
        if (shape.parentId !== pageId) {
          deletedGroupIds.push(shape.id)
          otherEffectedGroups.push(getShape(shape.parentId))
        }
      } else {
        page.shapes[shape.id].children = nextChildren
      }
    }

    // TODO: This code is copied from delete.command. Create a shared helper!

    // We also need to delete bindings that reference the deleted shapes
    _.values(bindings).forEach((binding) => {
      for (const id of [binding.toId, binding.fromId]) {
        // If the binding references a deleted shape...
        if (!page.shapes[id]) {
          // Delete this binding
          delete page.shapes[binding.id]

          // Let's also look each the bound shape...
          const shape = getShape(id)

          // If the bound shape has a handle that references the deleted binding...
          if (shape.handles) {
            Object.values(shape.handles)
              .filter((handle) => handle.bindingId === binding.id)
              .forEach((handle) => {
                // Unless we're currently deleting the shape, remove the
                // binding reference from the after patch
                if (!deletedGroupIds.includes(id)) {
                  handle.bindingId = undefined
                }
              })
          }
        }
      }
    })

    state.selectedIds = [groupId]
  }

  /**
   * Ungroup the selected groups.
   */
  @action ungroup = (ids = this.selectedIds, pageId = this.page.id) => {
    const {readOnly, getShape, pageById} = this,
      {page, state} = pageById[pageId]

    if (readOnly) return this
    this.undo_end()

    const groups = ids
        .map((id) => getShape(id, pageId))
        .filter((shape) => shape.type === 'group'),
      lookup = new Set<string>(groups.map(g => g.id))

    if (groups.length === 0) return this

    const {bindings} = page

    state.selectedIds = ids.filter((id) => !lookup.has(id))

    // The group shape
    groups
      .filter((shape) => !shape.isLocked)
      .forEach((groupShape) => {
        const shapesToReparent: TDShape[] = [],
          deletedGroupIds: string[] = []

        // Select its children in the next state
        groupShape.children.forEach((id) => {
          state.selectedIds.push(id)
          const shape = getShape(id, pageId)
          shapesToReparent.push(shape)
        })

        // We'll start placing the shapes at this childIndex
        const startingChildIndex = groupShape.childIndex

        // And we'll need to fit them under this child index
        const endingChildIndex = this.getChildIndexAbove(groupShape.id, pageId)

        delete page.shapes[groupShape.id]

        const step = (endingChildIndex - startingChildIndex) / shapesToReparent.length

        // An array of shapes in order by their child index
        const sortedShapes = shapesToReparent.sort((a, b) => a.childIndex - b.childIndex)

        // Reparent shapes to the page
        sortedShapes.forEach((shape, index) => {
          _.merge(page.shapes[shape.id], {
            parentId: pageId,
            childIndex: startingChildIndex + step * index
          })
        })

        // We also need to delete bindings that reference the deleted shapes
        _.values(bindings)
          .filter((binding) => binding.toId === groupShape.id || binding.fromId === groupShape.id)
          .forEach((binding) => {
            for (const id of [binding.toId, binding.fromId]) {
              // If the binding references the deleted group...
              if (!page.shapes[id]) {
                // Delete the binding
                delete bindings[binding.id]

                // Let's also look each the bound shape...
                const shape = getShape(id, pageId)

                // If the bound shape has a handle that references the deleted binding...
                if (shape.handles) {
                  Object.values(shape.handles)
                    .filter((handle) => handle.bindingId === binding.id)
                    .forEach((handle) => {
                      // Save the binding reference in the before patch

                      // Unless we're currently deleting the shape, remove the
                      // binding reference from the after patch
                      if (!deletedGroupIds.includes(id)) {
                        _.merge(shape[id].handles, {[handle.id]: {bindingId: undefined}})
                      }
                    })
                }
              }
            }
          })
      })
  }

  get getShapeUtil() { return this.shapeUtilFor}

  /* -------------------------------------------------- */
  /*                        Pages                       */
  /* -------------------------------------------------- */

  /**
   * Create a new page.
   * @param pageId (optional) The new page's id.
   */
  @action createPage = (id?: string) => {
    if (this.readOnly) return this
    this.undo_end()
    const {appState: {rendererBounds: {width, height}}, settings: {pages}, nextPageIndex} = this,
      center = [-width / 2, -height / 2]

    const page: TDPage = {
        id: id ?? guid(),
        name: `New Page`,
        childIndex: nextPageIndex,
        shapes: {},
        bindings: {}
      },
      pageState: WhiteboardPageState = {
        history: [],
        camera: {point: center, zoom: 1}
      }

    pages[page.id] = {page, state: pageState}
    this.currentPageId = page.id
  }

  /**
   * Change the current page.
   * @param pageId The new current page's id.
   */
  @action changePage = (pageId: string) => {
    this.undo_end()
    this.ui.currentPageId = pageId
  }

  /**
   * Rename a page.
   * @param pageId The id of the page to rename.
   * @param name The page's new name
   */
  @action renamePage = (pageId: string, name: string) => {
    const {settings: {pages}, readOnly} = this
    if (readOnly || !pages[pageId]) return
    this.undo_end()

    pages[pageId].page.name = name
  }

  /**
   * Duplicate a page.
   * @param pageId The id of the page to duplicate.
   */
  @action duplicatePage = (pageId: string): this => {
    if (this.readOnly) return this
    this.undo_end()

    const newId = Utils.uniqueId(),
      {page, myPageState: {camera}, settings: {pages}} = this

    const nextPage = {
      ...page,
      id: newId,
      name: page.name + ' Copy',
      shapes: Object.fromEntries(
        Object.entries(page.shapes).map(([id, shape]) => {
          return [
            id,
            {
              ...shape,
              parentId: shape.parentId === pageId ? newId : shape.parentId
            }
          ]
        })
      )
    }
    pages[nextPage.id] = {page: nextPage, state: _.cloneDeep(_.merge({}, DEFAULT_WHITEBOARD_PAGE_CONTENT.state, camera))}
  }

  /**
   * Delete a page.
   * @param pageId The id of the page to delete.
   */
  deletePage = (pageId?: string) => {
    const {readOnly, pagesInOrder, currentPageId, settings: {pages}, ui} = this
    if (readOnly || pagesInOrder.length <= 1) return
    this.undo_end()

    const currentIndex = pagesInOrder.findIndex((page) => page.id === pageId)

    delete pages[pageId]
    if (pageId === currentPageId) {
      if (currentIndex === pagesInOrder.length - 1) {
        ui.currentPageId = pagesInOrder[pagesInOrder.length - 2].id
      } else {
        ui.currentPageId = pagesInOrder[currentIndex + 1].id
      }
    } else {
      ui.currentPageId = currentPageId
    }
  }

  /// region Undo / Redo

  @observable undoIndex?: number = null

  @computed get isInUndo() {
    return _.isNumber(this.undoIndex)
  }

  @computed get canUndo() {
    const {history, undoIndex} = this
    return history.length > 1 && (_.isNull(undoIndex) || undoIndex > 0)
  }

  @action undo_end = () => {
    const {isInUndo, undoIndex, history} = this
    if (isInUndo) {
      this.history = history.slice(0, undoIndex)
      this.undoIndex = null
    }
  }

  @action undo = () => {
    let {undoIndex, canUndo, history} = this
    if (!canUndo) return

    const {page, appState} = this
    this.undoIndex = undoIndex == null ? history.length - 2 : undoIndex - 1
    _.assign(page, _.cloneDeep(this.history[this.undoIndex]))
    this.cleanupPageState()
  }

  @action cleanupPageState = () => {
    this.myPageState.selectedIds = this.selectedShapes.map(s => s.id)
  }

  @action redo = () => {
    if (!this.isInUndo) return

    this.undoIndex++

    if (this.undoIndex + 1 >= this.history.length) {
      this.undoIndex = null
      _.assign(this.page, _.cloneDeep(_.last(this.history)))
    } else {
      _.assign(this.page, _.cloneDeep(this.history[this.undoIndex]))
    }

    this.cleanupPageState()
  }

  /// endregion

  /// region Helpers

  getChildIndexAbove = (id: string, pageId: string = this.currentPageName): number => this._getChildIndexAbove(id, pageId)

  private _getChildIndexAbove = computedFn((id: string, pageId: string): number => {
    const {page} = this.pageById[pageId],
      shape = page.shapes[id]

    let siblings: TDShape[]

    if (shape.parentId === page.id) {
      siblings = Object.values<TDShape>(page.shapes)
        .filter((shape) => shape.parentId === page.id)
        .sort((a, b) => a.childIndex - b.childIndex)
    } else {
      const parent = page.shapes[shape.parentId]
      if (!parent.children) throw Error('No children in parent!')
      siblings = parent.children
        .map((childId) => page.shapes[childId])
        .sort((a, b) => a.childIndex - b.childIndex)
    }

    const index = siblings.indexOf(shape)

    const nextSibling = siblings[index + 1]

    if (!nextSibling) return shape.childIndex + 1

    return nextSibling.childIndex
  })

  /// endregion

  getTopChildIndex = (pageId: string = this.currentPageId) => this._getTopChildIndex(pageId)
  private _getTopChildIndex = computedFn((pageId: string) => {
    const shapes = this.getShapes(pageId)
    return shapes.length === 0
           ? 1
           : shapes
               .filter((shape) => shape.parentId === pageId)
               .sort((a, b) => b.childIndex - a.childIndex)[0].childIndex + 1
  })

  getShapes = (pageId: string = this.currentPageId): TDShape[] => this._getShapes(pageId)
  private _getShapes = computedFn((pageId: string): TDShape[] => {
    return Object.values(this.getPage(pageId)?.page?.shapes ?? {})
  })

  getPage = (pageId: string = this.currentPageId) => {
    return !pageId ? this.selectedPageInfo : this.pageById[pageId]
  }

  flattenPage = (pageId: string = this.currentPageId) => this._flattenPage(pageId)
  private _flattenPage = computedFn((pageId: string) => {
    return Object.values<TDShape>(this.getShapes(pageId))
      .sort((a, b) => a.childIndex - b.childIndex)
      .reduce<TDShape[]>((acc, shape) => [...acc, ...this.flattenShape(shape, pageId)], [])
  })

  flattenShape = (shape: TDShape, pageId: string = this.currentPageId) => this._flattenShape(shape, pageId)

  private _flattenShape = computedFn((shape: TDShape, pageId: string) => {
    const page = this.getPage(pageId),
      {getShape, flattenShape} = this
    return [
      shape,
      ...(shape.children ?? [])
        .map((childId) => getShape(childId, pageId))
        .sort((a, b) => a.childIndex - b.childIndex)
        .flatMap((shape) => flattenShape(shape, pageId))
    ]
  })

  @action resetSelectedShapeRotations = () => this.selectedShapes.forEach(s => s.rotation = 0)

  getShapeBounds = (s: WhiteboardShape) => {
    const {shapeUtilFor} = this, util = shapeUtilFor(s)
    return util ? util.getBounds(s) : null
  }

  /**
   * Drag and Drop from Catalog Tab -> Whiteboard Tab
   */
  @observable isDragCreatingShapesFromExternal = false
  @observable dragCreatedShapes: Array<WhiteboardShape> = []

  @observable.ref dragFromCatalogCardShapes: Array<CatalogItemCardShape> = []

  @action onDragEnter = (e: React.DragEvent) => {
    e.preventDefault()
    return

    // const {dragDrop: {dragItem}} = globalStores
    //
    // console.log(`Whiteboard - onDragEnter`, toJS(dragItem))
    //
    // if (dragItem?.target?.startsWith('vlp-') && dragItem.targetExtra?.store) {
    //   this.showDropToCreatePlaceholder = true
    //   this.mouseLocationRaw = {x: e.clientX, y: e.clientY}
    //
    //   const {mouseLocationVirtual: point} = this,
    //     {targetExtra: {store: {catalog}}} = dragItem
    //
    //   console.log(`Mouse point - `, point)
    //
    //   const shapes = this.dragFromCatalogCardShapes = this.createUtil.createBuilderCards(catalog, [dragItem.id, ...dragItem.extraIds].filter(_.identity), point)
    //   // shapes.forEach(s => s.isGhost = true)
    //   this.appState.selectedIds.replace(shapes.map(s => s.id))
    //   // this.camera.zoomToSelection()
    // }
  }

  @action onDragLeave = (e: React.DragEvent) => {
    const {dragDrop: {dragItem}} = globalStores
    // console.log(`Whiteboard - onDragLeave`, toJS(dragItem))
    this.showDropToCreatePlaceholder = false

    // this.dragFromCatalogCardShapes.forEach(s => s.isHidden = true)
    // this.tools.cancelSession()
  }

  onDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    return false
  }

  onDragEnd = (e: React.DragEvent) => {
    const {dragDrop: {dragItem}} = globalStores
    // console.log(`Whiteboard - onDragEnd`, toJS(dragItem))
    this.showDropToCreatePlaceholder = true

  }

  @observable showDropToCreatePlaceholder = false
  @observable isCreating = false

  /**
   * Don't put in action - we want to show each card as it's created
   * @param e
   */
  onDrop = async (e: React.DragEvent) => {
    // console.log(`onDrop`, e)
    this.showDropToCreatePlaceholder = true
    this.isCreating = true
    this.mouseLocationRaw = {x: e.clientX, y: e.clientY}

    try {
      const {dragDrop, dragDrop: {readDataTransfer}, catalog: {img: {readImageFromDataTransfer}}} = globalStores,
        {mouseLocationVirtual: point, createUtil, myPageState} = this

      let dragItem = dragDrop.dragItem
      if (_.isEmpty(dragItem)) {
        dragItem = readDataTransfer(e.dataTransfer, true)
      } else if (dragItem?.target?.startsWith('vlp-')) {
        const {createUtil: {createBuilderCards}} = this

        if (!_.isEmpty(dragItem.cardStores)) {
          const {cardStores} = dragItem

          //await utils.sleep(50) // let loader animate

          runInAction(() => {
            this.tab.selectInTabSet()
            this.tab.tabSet.isActive = true

            const shapes = this.dragFromCatalogCardShapes = createBuilderCards(cardStores, point, true)
            // shapes.forEach(s => s.isGhost = true)
            myPageState.selectedIds = shapes.map(s => s.id)
          })

          // this.camera.zoomToSelection()
        }
      }

      if (dragItem?.dragType === 'images') {
        const images = await readImageFromDataTransfer(e.dataTransfer)

        for (const image of images) {
          await createUtil.createImageShape(image)
        }
      }
    } finally {
      this.isCreating = false
    }
  }

  imageUrlFor = (shape: ImageShape) => {
    const {catalog: {imageUrlFor}} = this, {exId} = shape
    return imageUrlFor(exId)
  }

  @computed get selectedShapesInHorizontalOrder() {
    const {selectedShapes, shapeUtil: {getCenter}} = this
    return _.orderBy(selectedShapes, s => getCenter(s)[0])
  }

  getDistributions(initialShapes: TDShape[], type: DistributeType) {
    const entries = initialShapes.map((shape) => {
      const utils = this.shapeUtilFor(shape)
      return {
        id: shape.id,
        point: [...shape.point],
        bounds: utils.getBounds(shape),
        center: utils.getCenter(shape)
      }
    })

    const len = entries.length
    const commonBounds = Utils.getCommonBounds(entries.map(({bounds}) => bounds))

    const results: { id: string; prev: number[]; next: number[] }[] = []

    switch (type) {
      case 'horizontal': {
        const span = entries.reduce((a, c) => a + c.bounds.width, 0)

        if (span > commonBounds.width) {
          const left = entries.sort((a, b) => a.bounds.minX - b.bounds.minX)[0]

          const right = entries.sort((a, b) => b.bounds.maxX - a.bounds.maxX)[0]

          const entriesToMove = entries
            .filter((a) => a !== left && a !== right)
            .sort((a, b) => a.center[0] - b.center[0])

          const step = (right.center[0] - left.center[0]) / (len - 1)

          const x = left.center[0] + step

          entriesToMove.forEach(({id, point, bounds}, i) => {
            results.push({
              id,
              prev: point,
              next: [x + step * i - bounds.width / 2, bounds.minY]
            })
          })
        } else {
          const entriesToMove = entries.sort((a, b) => a.center[0] - b.center[0])

          let x = commonBounds.minX
          const step = (commonBounds.width - span) / (len - 1)

          entriesToMove.forEach(({id, point, bounds}) => {
            results.push({id, prev: point, next: [x, bounds.minY]})
            x += bounds.width + step
          })
        }
        break
      }
      case 'vertical': {
        const span = entries.reduce((a, c) => a + c.bounds.height, 0)

        if (span > commonBounds.height) {
          const top = entries.sort((a, b) => a.bounds.minY - b.bounds.minY)[0]

          const bottom = entries.sort((a, b) => b.bounds.maxY - a.bounds.maxY)[0]

          const entriesToMove = entries
            .filter((a) => a !== top && a !== bottom)
            .sort((a, b) => a.center[1] - b.center[1])

          const step = (bottom.center[1] - top.center[1]) / (len - 1)

          const y = top.center[1] + step

          entriesToMove.forEach(({id, point, bounds}, i) => {
            results.push({
              id,
              prev: point,
              next: [bounds.minX, y + step * i - bounds.height / 2]
            })
          })
        } else {
          const entriesToMove = entries.sort((a, b) => a.center[1] - b.center[1])

          let y = commonBounds.minY
          const step = (commonBounds.height - span) / (len - 1)

          entriesToMove.forEach(({id, point, bounds}) => {
            results.push({id, prev: point, next: [bounds.minX, y]})
            y += bounds.height + step
          })
        }

        break
      }
    }

    return results
  }

  /**
   * How large an image should we be asking cantaloupe for?
   *
   */
  @computed get catalogItemImageSizeRasterized() {
    return 1600
  }

  createDefaultStyle = () => {
    return createDefaultStyle()
  }

  @action selectShape = (shape: TDShape) => {
    this.myPageState.selectedIds = shape ? [shape.id] : []
  }

  simulatePressureSettings: StrokeOptions = {
    easing: (t) => Math.sin((t * Math.PI) / 2),
    simulatePressure: true
  }

  realPressureSettings: StrokeOptions = {
    easing: (t) => t * t,
    simulatePressure: false
  }

  getFreehandOptions = computedFn(({style: {strokeWidth}, points, isComplete}: DrawShape) : StrokeOptions => {
    const {appState: {toolSettings: {draw}}, simulatePressureSettings, realPressureSettings} = this,
      options: StrokeOptions = {
      ...draw,
      size: 1 + strokeWidth * 1.5,
      ...(points[1][2] === 0.5 ? simulatePressureSettings : realPressureSettings),
      last: isComplete
    }

    return options
  })
}

export const WhiteboardStoreContext = React.createContext<WhiteboardStore>(null)
export const useWhiteboardStore = () => React.useContext(WhiteboardStoreContext)

export const useTldrawApp = useWhiteboardStore

export type TldrawApp = WhiteboardStore



// WEBPACK FOOTER //
// ./src/modules/whiteboard/WhiteboardStore.tsx